import React, { useState, useEffect, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { UICustomizationData } from "../types"
import { useAppSelector } from "hooks/storeHooks"
import { ButtonPreview } from "../../../components/ButtonPreview"
import {
  ButtonDisplayVariant,
  ButtonIcon,
  DEFAULT_BUTTON_COLOR,
  DEFAULT_BUTTON_TEXT,
  DEFAULT_BUTTON_ICON,
  DEFAULT_SHOW_BUTTON_TEXT,
  DEFAULT_HAS_NO_ICON,
  DEFAULT_ICON_TEXT_SAME_LINE,
  DEFAULT_TEXT_COLOR,
  DEFAULT_BORDER_RADIUS,
  DEFAULT_BORDER_WIDTH,
  DEFAULT_BORDER_COLOR,
} from "../../../types/buttonCustomization"
import { typedEnv } from "../../../config/typedEnv"

interface UICustomizationWidgetPositioningProps {
  data: UICustomizationData
  onChange: (data: UICustomizationData) => void
  onSave?: () => void
  saveLoading?: boolean
}

export const UICustomizationWidgetPositioning = ({
  data: _data,
  onChange: _onChange,
  onSave: _onSave,
  saveLoading: _saveLoading,
}: UICustomizationWidgetPositioningProps) => {
  const { t } = useTranslation()
  const merchantId = useAppSelector((state) => state.user.userInfo.merchantId)

  const [selectedPosition, setSelectedPosition] = useState<ButtonDisplayVariant>(
    ButtonDisplayVariant.BOTTOM_LEFT
  )
  const [buttonColor, setButtonColor] = useState<string>(DEFAULT_BUTTON_COLOR)
  const [buttonText, setButtonText] = useState<string>(DEFAULT_BUTTON_TEXT)
  const [buttonIcon, setButtonIcon] = useState<ButtonIcon>(DEFAULT_BUTTON_ICON)
  const [showButtonText, setShowButtonText] = useState<boolean>(DEFAULT_SHOW_BUTTON_TEXT)
  const [hasNoIcon, setHasNoIcon] = useState<boolean>(DEFAULT_HAS_NO_ICON)
  const [iconTextSameLine, setIconTextSameLine] = useState<boolean>(DEFAULT_ICON_TEXT_SAME_LINE)
  const [textColor, setTextColor] = useState<string>(DEFAULT_TEXT_COLOR)
  const [borderRadius, setBorderRadius] = useState<string>(DEFAULT_BORDER_RADIUS)
  const [borderWidth, setBorderWidth] = useState<string>(DEFAULT_BORDER_WIDTH)
  const [borderColor, setBorderColor] = useState<string>(DEFAULT_BORDER_COLOR)
  const [loading, setLoading] = useState(true)

  const fetchCurrentSettings = useCallback(async () => {
    try {
      setLoading(true)
      const response = await fetch(
        `${typedEnv.VITE_TIENDANUBE_API_URL}/api/v1/merchant/button-display?merchantId=${merchantId}`
      )
      if (response.ok) {
        const result = await response.json()
        const position = result.buttonDisplayVariant || ButtonDisplayVariant.BOTTOM_LEFT
        const color = result.buttonColor || DEFAULT_BUTTON_COLOR
        const text = result.buttonText || DEFAULT_BUTTON_TEXT
        const icon = result.buttonIcon || DEFAULT_BUTTON_ICON
        const showText =
          result.showButtonText !== undefined ? result.showButtonText : DEFAULT_SHOW_BUTTON_TEXT
        const noIcon = result.hasNoIcon !== undefined ? result.hasNoIcon : DEFAULT_HAS_NO_ICON
        const sameLine =
          result.iconTextSameLine !== undefined
            ? result.iconTextSameLine
            : DEFAULT_ICON_TEXT_SAME_LINE
        const txtColor = result.textColor || DEFAULT_TEXT_COLOR
        const rad = result.borderRadius || DEFAULT_BORDER_RADIUS
        const width = result.borderWidth || DEFAULT_BORDER_WIDTH
        const borderClr = result.borderColor || DEFAULT_BORDER_COLOR

        setSelectedPosition(position)
        setButtonColor(color)
        setButtonText(text)
        setButtonIcon(icon)
        setShowButtonText(showText)
        setHasNoIcon(noIcon)
        setIconTextSameLine(sameLine)
        setTextColor(txtColor)
        setBorderRadius(rad)
        setBorderWidth(width)
        setBorderColor(borderClr)
      }
    } catch (error) {
      console.error("Failed to fetch current settings:", error)
    } finally {
      setLoading(false)
    }
  }, [merchantId])

  useEffect(() => {
    fetchCurrentSettings()
  }, [fetchCurrentSettings])

  const handlePositionChange = (position: ButtonDisplayVariant) => {
    setSelectedPosition(position)
    updateDataInParent(
      position,
      buttonColor,
      buttonText,
      buttonIcon,
      showButtonText,
      hasNoIcon,
      iconTextSameLine,
      textColor,
      borderRadius,
      borderWidth,
      borderColor
    )
  }

  const handleColorChange = (value: string) => {
    setButtonColor(value)
    updateDataInParent(
      selectedPosition,
      value,
      buttonText,
      buttonIcon,
      showButtonText,
      hasNoIcon,
      iconTextSameLine,
      textColor,
      borderRadius,
      borderWidth,
      borderColor
    )
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value.slice(0, 30)
    setButtonText(newText)
    updateDataInParent(
      selectedPosition,
      buttonColor,
      newText,
      buttonIcon,
      showButtonText,
      hasNoIcon,
      iconTextSameLine,
      textColor,
      borderRadius,
      borderWidth,
      borderColor
    )
  }

  const handleIconChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newIcon = e.target.value as ButtonIcon
    setButtonIcon(newIcon)
    updateDataInParent(
      selectedPosition,
      buttonColor,
      buttonText,
      newIcon,
      showButtonText,
      hasNoIcon,
      iconTextSameLine,
      textColor,
      borderRadius,
      borderWidth,
      borderColor
    )
  }

  const handleShowTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newShowText = e.target.checked
    setShowButtonText(newShowText)
    updateDataInParent(
      selectedPosition,
      buttonColor,
      buttonText,
      buttonIcon,
      newShowText,
      hasNoIcon,
      iconTextSameLine,
      textColor,
      borderRadius,
      borderWidth,
      borderColor
    )
  }

  const handleHasNoIconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newHasNoIcon = !e.target.checked
    setHasNoIcon(newHasNoIcon)
    updateDataInParent(
      selectedPosition,
      buttonColor,
      buttonText,
      buttonIcon,
      showButtonText,
      newHasNoIcon,
      iconTextSameLine,
      textColor,
      borderRadius,
      borderWidth,
      borderColor
    )
  }

  const handleIconTextSameLineChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newIconTextSameLine = e.target.checked
    setIconTextSameLine(newIconTextSameLine)

    // When enabling "Icon and Text on Same Line", automatically set showButtonText to true and hasNoIcon to false
    const newShowButtonText = newIconTextSameLine ? true : showButtonText
    const newHasNoIcon = newIconTextSameLine ? false : hasNoIcon

    if (newIconTextSameLine) {
      setShowButtonText(true)
      setHasNoIcon(false)
    }

    updateDataInParent(
      selectedPosition,
      buttonColor,
      buttonText,
      buttonIcon,
      newShowButtonText,
      newHasNoIcon,
      newIconTextSameLine,
      textColor,
      borderRadius,
      borderWidth,
      borderColor
    )
  }

  const handleTextColorChange = (value: string) => {
    setTextColor(value)
    updateDataInParent(
      selectedPosition,
      buttonColor,
      buttonText,
      buttonIcon,
      showButtonText,
      hasNoIcon,
      iconTextSameLine,
      value,
      borderRadius,
      borderWidth,
      borderColor
    )
  }

  const handleBorderRadiusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newBorderRadius = e.target.value
    setBorderRadius(newBorderRadius)
    updateDataInParent(
      selectedPosition,
      buttonColor,
      buttonText,
      buttonIcon,
      showButtonText,
      hasNoIcon,
      iconTextSameLine,
      textColor,
      newBorderRadius,
      borderWidth,
      borderColor
    )
  }

  const handleBorderWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const newBorderWidth = value ? `${value}px` : ""
    setBorderWidth(newBorderWidth)
    // Only update parent if value has changed to avoid infinite loops
    updateDataInParent(
      selectedPosition,
      buttonColor,
      buttonText,
      buttonIcon,
      showButtonText,
      hasNoIcon,
      iconTextSameLine,
      textColor,
      borderRadius,
      newBorderWidth,
      borderColor
    )
  }

  const handleBorderColorChange = (value: string) => {
    setBorderColor(value)
    updateDataInParent(
      selectedPosition,
      buttonColor,
      buttonText,
      buttonIcon,
      showButtonText,
      hasNoIcon,
      iconTextSameLine,
      textColor,
      borderRadius,
      borderWidth,
      value
    )
  }

  const updateDataInParent = (
    position: ButtonDisplayVariant,
    color: string,
    text: string,
    icon: ButtonIcon,
    showText: boolean,
    noIcon: boolean,
    sameLine: boolean,
    textColorValue: string,
    borderRadiusValue: string,
    borderWidthValue: string,
    borderColorValue: string
  ) => {
    const widgetPositioningData: any = {
      buttonDisplayVariant: position,
      buttonColor: color,
      buttonText: text,
      buttonIcon: icon,
      showButtonText: showText,
      hasNoIcon: noIcon,
      iconTextSameLine: sameLine,
      textColor: textColorValue,
    }

    // Always include border properties to ensure they are saved (even if empty string)
    widgetPositioningData.borderRadius = borderRadiusValue || ""
    widgetPositioningData.borderWidth = borderWidthValue || ""
    widgetPositioningData.borderColor = borderColorValue || ""

    _onChange({
      ..._data,
      widgetPositioning: widgetPositioningData,
    })
  }

  const ProductPageExample = ({
    showVTOButton,
    vtoPosition,
    vtoColor,
    vtoText,
    vtoIcon,
    vtoShowText,
    vtoHasNoIcon,
    vtoIconTextSameLine,
    vtoBorderRadius,
    vtoBorderWidth,
    vtoBorderColor,
  }: {
    showVTOButton: boolean
    vtoPosition: ButtonDisplayVariant
    vtoColor: string
    vtoText: string
    vtoIcon: ButtonIcon
    vtoShowText: boolean
    vtoHasNoIcon: boolean
    vtoIconTextSameLine: boolean
    vtoBorderRadius?: string
    vtoBorderWidth?: string
    vtoBorderColor?: string
  }) => (
    <div className="relative w-[400px] h-[240px] bg-white rounded-lg border border-gray-300 overflow-hidden">
      <div className="flex h-full">
        <div className="w-1/2 p-2 h-full">
          <div className="flex gap-2 h-full">
            <div className="flex flex-col gap-1 w-10 h-full justify-start">
              <div className="w-[30px] h-[30px] bg-gray-200 rounded" />
              <div className="w-[30px] h-[30px] bg-gray-200 rounded" />
              <div className="w-[30px] h-[30px] bg-gray-200 rounded" />
              <div className="w-[30px] h-[30px] bg-gray-200 rounded" />
            </div>

            <div className="flex-1 h-full bg-gray-200 rounded relative max-h-[180px]">
              {showVTOButton &&
                (vtoPosition === ButtonDisplayVariant.PRODUCT_IMAGE ||
                  vtoPosition === ButtonDisplayVariant.PRODUCT_IMAGE_LEFT) && (
                  <div
                    className={`absolute top-2 ${
                      vtoPosition === ButtonDisplayVariant.PRODUCT_IMAGE ? "right-2" : "left-2"
                    }`}
                  >
                    <ButtonPreview
                      color={vtoColor}
                      text={vtoText}
                      icon={vtoIcon}
                      showText={vtoShowText}
                      position={vtoPosition}
                      hasNoIcon={vtoHasNoIcon}
                      iconTextSameLine={vtoIconTextSameLine}
                      textColor={textColor}
                      borderRadius={vtoBorderRadius}
                      borderWidth={vtoBorderWidth}
                      borderColor={vtoBorderColor}
                    />
                  </div>
                )}
            </div>
          </div>
        </div>

        <div className="w-1/2 p-2">
          <div className="text-sm font-semibold mb-2">{"Product Name"}</div>
          <div className="text-sm font-bold mb-2 text-blue-600">{"$89.99"}</div>
          <div className="h-3 bg-gray-200 rounded mb-1" />
          <div className="h-3 bg-gray-200 rounded mb-1 w-4/5" />
          <div className="h-3 bg-gray-200 rounded mb-1 w-3/5" />
          <div className="h-8 bg-gray-200 rounded mt-3" />
        </div>
      </div>

      {showVTOButton &&
        (vtoPosition === ButtonDisplayVariant.BOTTOM_LEFT ||
          vtoPosition === ButtonDisplayVariant.BOTTOM_RIGHT) && (
          <div
            className={`absolute bottom-2 ${
              vtoPosition === ButtonDisplayVariant.BOTTOM_LEFT ? "left-2" : "right-2"
            }`}
          >
            <ButtonPreview
              color={vtoColor}
              text={vtoText}
              icon={vtoIcon}
              showText={vtoShowText}
              position={vtoPosition}
              hasNoIcon={vtoHasNoIcon}
              iconTextSameLine={vtoIconTextSameLine}
              textColor={textColor}
              borderRadius={vtoBorderRadius}
              borderWidth={vtoBorderWidth}
              borderColor={vtoBorderColor}
            />
          </div>
        )}
    </div>
  )

  const PositionOption = ({
    value,
    title,
    description,
    position,
  }: {
    value: ButtonDisplayVariant
    title: string
    description: string
    position: ButtonDisplayVariant
  }) => (
    <div className="flex-1 p-4 bg-[#181826] rounded-lg">
      <label className="flex items-start cursor-pointer">
        <input
          type="radio"
          name="position"
          value={value}
          checked={selectedPosition === value}
          onChange={() => handlePositionChange(value)}
          disabled={loading}
          className="mt-1 mr-3"
        />
        <div className="flex-1">
          <div className="text-sm font-semibold mb-1 text-white whitespace-pre-line">{title}</div>
          <div className="text-xs text-gray-300 whitespace-pre-line">{description}</div>
        </div>
      </label>

      <div className="">
        <div className="text-xs font-semibold mb-2 text-gray-900">
          {t("widgetPositioning.example")}:
        </div>
        <ProductPageExample
          showVTOButton={true}
          vtoPosition={position}
          vtoColor={buttonColor}
          vtoText={buttonText}
          vtoIcon={buttonIcon}
          vtoShowText={showButtonText}
          vtoHasNoIcon={hasNoIcon}
          vtoIconTextSameLine={iconTextSameLine}
          vtoBorderRadius={borderRadius}
          vtoBorderWidth={borderWidth}
          vtoBorderColor={borderColor}
        />
      </div>
    </div>
  )

  return (
    <div className="relative">
      <div className="px-6 pb-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
            <p className="text-white text-lg font-semibold">
              {t("loading") || "Loading customization settings..."}
            </p>
            <p className="text-gray-400 text-sm mt-2">
              {t("widgetPositioning.loadingDescription") ||
                "Fetching your button configuration from Tiendanube..."}
            </p>
          </div>
        ) : (
          <>
            {/* Button Customization Section */}
            <div className="p-4 bg-[#181826] rounded-lg mb-6">
              <h3 className="text-xl font-bold mb-3 text-white">
                {t("widgetPositioning.buttonCustomization.title")}
              </h3>

              <p className="text-sm text-gray-300 mb-4">
                {t("widgetPositioning.buttonCustomization.description")}
              </p>

              <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <p className="text-sm text-blue-300">
                  {t("widgetPositioning.buttonCustomization.updateInfo")}
                </p>
              </div>

              <div className="flex gap-6">
                {/* Preview */}
                <div className="flex-1">
                  <div className="text-sm font-semibold mb-3 text-white">
                    {t("widgetPositioning.buttonCustomization.preview")}
                  </div>
                  <div className="p-8 bg-white rounded-lg flex justify-center items-center min-h-[300px]">
                    <div style={{ transform: "scale(3)", transformOrigin: "center" }}>
                      <ButtonPreview
                        color={buttonColor}
                        text={buttonText}
                        icon={buttonIcon}
                        showText={showButtonText}
                        position={selectedPosition}
                        hasNoIcon={hasNoIcon}
                        iconTextSameLine={iconTextSameLine}
                        textColor={textColor}
                        borderRadius={borderRadius}
                        borderWidth={borderWidth}
                        borderColor={borderColor}
                      />
                    </div>
                  </div>
                </div>

                {/* Controls */}
                <div className="flex-1 flex flex-col gap-3">
                  {/* Color Picker */}
                  <div>
                    <label className="text-sm font-semibold mb-2 block text-white">
                      {t("widgetPositioning.buttonCustomization.color")}
                    </label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        value={buttonColor}
                        onChange={(e) => handleColorChange(e.target.value)}
                        disabled={loading}
                        className="w-[50px] h-9 border border-gray-600 rounded cursor-pointer bg-gray-800"
                      />
                      <input
                        type="text"
                        value={buttonColor}
                        onChange={(e) => handleColorChange(e.target.value)}
                        disabled={loading}
                        placeholder="#000000"
                        className="flex-1 px-3 py-2 border border-gray-600 rounded bg-gray-800 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Text Color Picker */}
                  <div>
                    <label className="text-sm font-semibold mb-2 block text-white">
                      {t("widgetPositioning.buttonCustomization.textColor")}
                    </label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        value={textColor}
                        onChange={(e) => handleTextColorChange(e.target.value)}
                        disabled={loading}
                        className="w-[50px] h-9 border border-gray-600 rounded cursor-pointer bg-gray-800"
                      />
                      <input
                        type="text"
                        value={textColor}
                        onChange={(e) => handleTextColorChange(e.target.value)}
                        disabled={loading}
                        placeholder="#FFFFFF"
                        className="flex-1 px-3 py-2 border border-gray-600 rounded bg-gray-800 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Text Input */}
                  <div>
                    <label className="text-sm font-semibold mb-2 block text-white">
                      {t("widgetPositioning.buttonCustomization.text")}
                    </label>
                    <input
                      type="text"
                      value={buttonText}
                      onChange={handleTextChange}
                      disabled={loading}
                      maxLength={30}
                      placeholder={t("widgetPositioning.buttonCustomization.textPlaceholder")}
                      className="w-full px-3 py-2 border border-gray-600 rounded bg-gray-800 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      {t("widgetPositioning.buttonCustomization.textHelp")} ({buttonText.length}/30)
                    </p>
                  </div>

                  {/* Icon Selector */}
                  <div>
                    <label className="text-sm font-semibold mb-2 block text-white">
                      {t("widgetPositioning.buttonCustomization.icon")}
                    </label>
                    <select
                      value={buttonIcon}
                      onChange={handleIconChange}
                      disabled={loading}
                      className="w-full px-3 py-2 border border-gray-600 rounded bg-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {Object.values(ButtonIcon).map((icon) => (
                        <option key={icon} value={icon}>
                          {t(`widgetPositioning.buttonCustomization.icons.${icon}`)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Icon and Text Same Line Toggle */}
                  <div>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={iconTextSameLine}
                        onChange={handleIconTextSameLineChange}
                        disabled={loading}
                        className="mr-2"
                      />
                      <span className="text-sm text-white">
                        {t("widgetPositioning.buttonCustomization.iconTextSameLine")}
                      </span>
                    </label>
                  </div>

                  {/* Show Text Toggle */}
                  <div>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showButtonText}
                        onChange={handleShowTextChange}
                        disabled={loading || iconTextSameLine}
                        className="mr-2"
                      />
                      <span className="text-sm text-white">
                        {t("widgetPositioning.buttonCustomization.showText")}
                      </span>
                    </label>
                  </div>

                  {/* Show Icon Toggle */}
                  <div>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!hasNoIcon}
                        onChange={handleHasNoIconChange}
                        disabled={loading || iconTextSameLine}
                        className="mr-2"
                      />
                      <span className="text-sm text-white">
                        {t("widgetPositioning.buttonCustomization.showIcon")}
                      </span>
                    </label>
                  </div>

                  {/* Border Radius */}
                  <div>
                    <label className="text-sm font-semibold mb-2 block text-white">
                      {t("widgetPositioning.buttonCustomization.borderRadius")}{" "}
                      {t("widgetPositioning.buttonCustomization.px")}
                    </label>
                    <input
                      type="number"
                      value={borderRadius ? borderRadius.replace("px", "") : ""}
                      onChange={(e) =>
                        handleBorderRadiusChange({
                          target: { value: e.target.value ? `${e.target.value}px` : "" },
                        } as any)
                      }
                      disabled={loading}
                      placeholder="e.g., 12"
                      min="0"
                      className="w-full px-3 py-2 border border-gray-600 rounded bg-gray-800 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Border Width */}
                  <div>
                    <label className="text-sm font-semibold mb-2 block text-white">
                      {t("widgetPositioning.buttonCustomization.borderWidth")}{" "}
                      {t("widgetPositioning.buttonCustomization.px")}
                    </label>
                    <input
                      type="number"
                      value={borderWidth ? borderWidth.replace("px", "") : ""}
                      onChange={handleBorderWidthChange}
                      disabled={loading}
                      placeholder="e.g., 2"
                      min="0"
                      className="w-full px-3 py-2 border border-gray-600 rounded bg-gray-800 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Border Color */}
                  <div>
                    <label className="text-sm font-semibold mb-2 block text-white">
                      {t("widgetPositioning.buttonCustomization.borderColor")}
                    </label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        value={borderColor}
                        onChange={(e) => handleBorderColorChange(e.target.value)}
                        disabled={loading}
                        className="w-[50px] h-9 border border-gray-600 rounded cursor-pointer bg-gray-800"
                      />
                      <input
                        type="text"
                        value={borderColor}
                        onChange={(e) => handleBorderColorChange(e.target.value)}
                        disabled={loading}
                        placeholder="#FFFFFF"
                        className="flex-1 px-3 py-2 border border-gray-600 rounded bg-gray-800 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Position Selection */}
            <h3 className="text-xl font-bold mb-3 text-white">
              {t("widgetPositioning.title").replace("\\n", "")}
            </h3>

            <div className="flex flex-col gap-4">
              {/* First row: Bottom positions */}
              <div className="flex gap-4">
                <PositionOption
                  value={ButtonDisplayVariant.BOTTOM_LEFT}
                  title={t("widgetPositioning.bottomLeft.title")}
                  description={t("widgetPositioning.bottomLeft.description")}
                  position={ButtonDisplayVariant.BOTTOM_LEFT}
                />

                <PositionOption
                  value={ButtonDisplayVariant.BOTTOM_RIGHT}
                  title={t("widgetPositioning.bottomRight.title")}
                  description={t("widgetPositioning.bottomRight.description")}
                  position={ButtonDisplayVariant.BOTTOM_RIGHT}
                />
              </div>

              {/* Second row: Product image positions */}
              <div className="flex gap-4">
                <PositionOption
                  value={ButtonDisplayVariant.PRODUCT_IMAGE}
                  title={t("widgetPositioning.productImage.title")}
                  description={t("widgetPositioning.productImage.description")}
                  position={ButtonDisplayVariant.PRODUCT_IMAGE}
                />

                <PositionOption
                  value={ButtonDisplayVariant.PRODUCT_IMAGE_LEFT}
                  title={t("widgetPositioning.productImageLeft.title")}
                  description={t("widgetPositioning.productImageLeft.description")}
                  position={ButtonDisplayVariant.PRODUCT_IMAGE_LEFT}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
